import { AnalyticsProvider } from '@cheqroom/analytics';
import { ApiClient, ApiClientContext, createApiClient } from '@cheqroom/api';
import { DateTime } from '@cheqroom/date-time';
import { StorageProvider, useEventListener } from '@cheqroom/hooks';
import { Portal, ThemeProvider } from '@cheqroom/ui';
import { ModalProvider } from '@cheqroom/ui/Modal';
import { ErrorBoundary } from '@sentry/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { FC, Suspense, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { Toaster } from 'sonner';

import { BrowserTimezoneSync } from './components/BrowserTimezoneSync/BrowserTimezoneSync';
import { AccessibilityProvider } from './context/Accessibility.context';
import { AuthenticationProvider } from './context/Authentication.context';
import { FeatureFlagsProvider } from './context/FeatureFlag.context';
import Client from './services/analytics';
import Authentication from './services/authentication';
import i18n from './services/i18n';
import { queryClient } from './services/react-query';

interface ComponentEvent {
	component: string;
	props: Record<string, unknown>;
	target: HTMLElement;
}

const API_URL = process.env.API_URL_V3 as string;

const container = document.getElementById('react-root') as HTMLElement;
const root = createRoot(container);

export const Root: FC<{ apiClient: ApiClient }> = ({ apiClient }) => {
	const [components, setComponents] = useState<ComponentEvent[]>([]);

	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		const lokaliseMode = params.get('lokalise_mode');

		if (process.env.ENVIRONMENT !== 'production' && lokaliseMode === 'debug') {
			void i18n.changeLanguage('cimode');
		}
	}, []);

	useEventListener('cheqroom-add-component', (event) => {
		setComponents((components) => [...components, (event as CustomEvent<ComponentEvent>).detail]);
	});

	useEventListener('cheqroom-update-component', (event) => {
		const updated = (event as CustomEvent<ComponentEvent>).detail;
		setComponents((components) => {
			return components.map((component) => {
				if (component.target.id === updated.target.id) return updated;
				return component;
			});
		});
	});

	useEventListener('cheqroom-remove-component', (event) => {
		const target = (event as CustomEvent<ComponentEvent>).detail.target;
		setComponents((components) => components.filter((component) => component.target.id !== target.id));
	});

	return (
		<ThemeProvider>
			<AccessibilityProvider>
				<ApiClientContext.Provider value={{ apiClient }}>
					<AuthenticationProvider>
						<FeatureFlagsProvider>
							<AnalyticsProvider analytics={Client}>
								<QueryClientProvider client={queryClient}>
									<Toaster />
									<ModalProvider>
										<StorageProvider>
											<BrowserTimezoneSync />
											{components.map((component) => (
												<Portal key={component.target.id} target={component.target}>
													<Suspense fallback={null}>
														<ErrorBoundary>
															<component.component {...component.props} />
														</ErrorBoundary>
													</Suspense>
												</Portal>
											))}
										</StorageProvider>
									</ModalProvider>
								</QueryClientProvider>
							</AnalyticsProvider>
						</FeatureFlagsProvider>
					</AuthenticationProvider>
				</ApiClientContext.Provider>
			</AccessibilityProvider>
		</ThemeProvider>
	);
};

export const mount = (): void => {
	// Opening hours are workspace wall-clock; booking datetimes persist as user floating values.
	// Resolver feeds DateTime.fromWorkspaceWallClock when snapping open/close.
	DateTime.setWorkspaceTimeZoneResolver(() => window.global?.central?.group?.timezone);

	const apiClient = createApiClient(API_URL, Authentication.getAuthClient(), {
		onSessionExpired: () => {
			Authentication.redirectToLogin();
		},
	});

	root.render(<Root apiClient={apiClient} />);
};

export const unmount = (): void => {
	root.unmount();
};
